import clsx from 'clsx';
import { Trans, useTranslation } from 'next-i18next/pages';
import Image, { StaticImageData } from 'next/image';
import { useRouter } from 'next/router';
import React, { ReactNode, useCallback } from 'react';

import CheckList from '../list/CheckList.component';

interface AnonymousWrapperProps {
  anonymous?: boolean;
  blurStrength?: 'normal' | 'md' | 'lg' | 'xl' | '2xl';
  children: ReactNode;
  detailed?: boolean;
  opacity?: '0' | '10' | '20' | '30' | '40' | '50' | '60' | '70' | '80' | '90' | '100';
  textPositioning?: 'left' | 'center';
  href?: string;
  img?: string | StaticImageData;
}

const getBlurClasses = (blurStrength: AnonymousWrapperProps['blurStrength']): string => {
  switch (blurStrength) {
    case 'md':
      return 'filter blur-md';
    case 'lg':
      return 'filter blur-md';
    case 'xl':
      return 'filter blur-xl';
    case '2xl':
      return 'filter blur-2xl';
    default:
      return 'filter blur-sm';
  }
};

export const AnonymousWrapper: React.FC<AnonymousWrapperProps> = ({
  anonymous,
  blurStrength = 'xl',
  children,
  detailed = false,
  opacity = '70',
  textPositioning = 'center',
  href = '#',
  img,
}) => {
  const { t } = useTranslation('job', { keyPrefix: 'anonymousWrapper' });
  const router = useRouter();

  const handleClick = useCallback(() => {
    router.push(href);
  }, [href, router]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        router.push(href);
      }
    },
    [href, router],
  );

  if (!anonymous) return children;

  return (
    <div
      role='link'
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      data-testid='anonymous-wrapper'
      className={clsx('grid-cols-auto grid-rows-auto grid w-full cursor-pointer', detailed && 'min-h-[300px]')}>
      <div
        className={clsx(
          getBlurClasses(blurStrength),
          `opacity-${opacity} pointer-events-none col-start-1 row-start-1 max-h-[300px] overflow-x-hidden overflow-y-clip`,
        )}>
        {children}
      </div>
      {detailed ? (
        <div className='relative z-10 col-start-1 row-start-1 flex place-self-center'>
          <div className='relative mr-7 hidden h-70 w-60 md:block'>{img && <Image alt='' className='object-contain' fill src={img} />}</div>
          <div className='m-2 flex flex-col justify-center gap-4'>
            <div className='text-default text-center text-xl font-medium md:items-start md:text-left'>
              <Trans
                components={{ span: <span className='font-handwriting text-primary text-3xl font-light' /> }}
                i18nKey='ctaDetailed'
                t={t}
              />
            </div>
            <CheckList checkListItems={[t('benefit1'), t('benefit2'), t('benefit3')]} />
          </div>
        </div>
      ) : (
        <div
          className={clsx(
            'text-default relative z-10 col-start-1 row-start-1 place-self-center text-center text-lg font-medium sm:text-left',
            textPositioning === 'left' && 'w-full lg:ml-6',
          )}>
          <Trans components={{ span: <span className='font-handwriting text-primary text-xl font-normal' /> }} i18nKey='ctaMinimal' t={t} />
        </div>
      )}
    </div>
  );
};

export default AnonymousWrapper;
